import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getPublicPlatformConfig } from '@/api/platformConfig';
import {
  FAILSAFE_PLATFORM_BROWSE_MODE,
  isOpenBrowseMode,
  PLATFORM_BROWSE_MODE_CHANGED_EVENT,
  type PlatformBrowseMode,
} from '@/constants/platformBrowseMode';
import { debug } from '@/utils/logger';

const REFRESH_INTERVAL_MS = 60_000;

export interface PlatformBrowseModeContextValue {
  browseMode: PlatformBrowseMode;
  isOpenBrowse: boolean;
  isClosedBrowse: boolean;
  isLoading: boolean;
  loadError: boolean;
  refreshBrowseMode: () => Promise<void>;
}

const PlatformBrowseModeContext = createContext<PlatformBrowseModeContextValue | null>(
  null
);

export function PlatformBrowseModeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [browseMode, setBrowseMode] = useState<PlatformBrowseMode>(
    FAILSAFE_PLATFORM_BROWSE_MODE
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const refreshBrowseMode = useCallback(async () => {
    try {
      const config = await getPublicPlatformConfig();
      setBrowseMode(config.browse_mode);
      setLoadError(false);
      debug('[PlatformBrowseMode] Loaded browse mode', {
        browse_mode: config.browse_mode,
      });
    } catch {
      setBrowseMode(FAILSAFE_PLATFORM_BROWSE_MODE);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshBrowseMode();
  }, [refreshBrowseMode]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void refreshBrowseMode();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [refreshBrowseMode]);

  useEffect(() => {
    const onModeChanged = () => {
      void refreshBrowseMode();
    };
    window.addEventListener(PLATFORM_BROWSE_MODE_CHANGED_EVENT, onModeChanged);
    return () => {
      window.removeEventListener(PLATFORM_BROWSE_MODE_CHANGED_EVENT, onModeChanged);
    };
  }, [refreshBrowseMode]);

  const value = useMemo<PlatformBrowseModeContextValue>(
    () => ({
      browseMode,
      isOpenBrowse: isOpenBrowseMode(browseMode),
      isClosedBrowse: !isOpenBrowseMode(browseMode),
      isLoading,
      loadError,
      refreshBrowseMode,
    }),
    [browseMode, isLoading, loadError, refreshBrowseMode]
  );

  return (
    <PlatformBrowseModeContext.Provider value={value}>
      {children}
    </PlatformBrowseModeContext.Provider>
  );
}

export function usePlatformBrowseMode(): PlatformBrowseModeContextValue {
  const context = useContext(PlatformBrowseModeContext);
  if (!context) {
    throw new Error(
      'usePlatformBrowseMode must be used within a PlatformBrowseModeProvider'
    );
  }
  return context;
}
